import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { AccountingInvoiceJson } from "@/api/accounting.tsx"
import { exactCurrencyFormatter } from "@/utils"
import { Check, ChevronsUpDown, FileText, X } from "lucide-react"
import { useMemo, useRef, useState } from "preact/hooks"
import { useOrgSettings } from "@/org_settings/state.tsx"
import { format, parseISO } from "date-fns"
import {
  dedupe_invoices_by_id,
  describe_invoice_links,
  invoice_outstanding_tag,
  picker_invoice_list,
  RECOMMENDED_INVOICE_BADGE_MIN_SCORE,
  score_accounting_invoice_match,
  type InvoiceLinkSummary,
  type InvoiceRecommendationContext,
} from "@/accounting/cash_application_display.ts"

const INTEGRATION_LABELS: Record<string, string> = {
  sageintacct: "Sage Intacct",
  quickbooks: "QuickBooks",
  xero: "Xero",
}

/**
 * Cap on how many invoice rows are rendered at once. Invoices are usually scoped to the check's
 * customer (a few dozen–hundreds), but the fetch falls back to the org's entire invoice history when
 * the customer has none — rendering 100k DOM nodes freezes the browser. The list is sorted by match
 * quality before slicing, so the best matches always render; typing narrows the rest.
 */
const MAX_RENDERED_INVOICES = 200

function get_integration_label(integration: string | null): string {
  if (!integration) {
    return "Accounting"
  }
  return INTEGRATION_LABELS[integration.toLowerCase()] || integration
}

interface AccountingInvoicePickerProps {
  invoices: AccountingInvoiceJson[]
  value: string | null
  onChange: (accounting_invoice_id: string | null) => void
  recommendationContext: InvoiceRecommendationContext
  reservedByOtherRows: Set<string>
  /** Invoices another line of this check settles only in part, with the cents still unsettled. */
  sharedRoomCents?: Map<string, number>
  /** Per invoice, what lines on other checks settle of it — shown as a hint, never a reservation. */
  linkSummaries?: Map<string, InvoiceLinkSummary>
  disabled?: boolean
  className?: string
}

export function AccountingInvoicePicker({
  invoices,
  value,
  onChange,
  recommendationContext,
  reservedByOtherRows,
  sharedRoomCents,
  linkSummaries,
  disabled,
  className,
}: AccountingInvoicePickerProps) {
  const orgSettings = useOrgSettings()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const commandListRef = useRef<HTMLDivElement>(null)

  const integration_label = get_integration_label(orgSettings.accounting_integration)

  const current = useMemo(() => invoices.find((i) => i.id === value) ?? null, [invoices, value])

  // One row per invoice: the list carries one row per link, and an invoice paid across several
  // checks would otherwise appear once per check.
  const unique_invoices = useMemo(() => dedupe_invoices_by_id(invoices), [invoices])
  // Keyed on the context's values, not its identity: callers pass a fresh `{ invoice_number,
  // amount_dollars }` literal every render, which used to bust every memo below on every render.
  const { invoice_number: context_invoice_number, amount_dollars: context_amount, po_number: context_po } = recommendationContext
  const context = useMemo(
    () => ({ invoice_number: context_invoice_number, amount_dollars: context_amount, po_number: context_po }),
    [context_invoice_number, context_amount, context_po]
  )
  // Rank only while the dropdown is open — a closed picker shows just the selection. The apply page
  // renders one picker per invoice line (188 on some Walmart checks) over the customer's whole invoice
  // list (9,978 for Walmart), so ranking closed pickers on every render froze the page.
  const invoice_list = useMemo(
    () => (open ? picker_invoice_list(unique_invoices, searchQuery, context, value, MAX_RENDERED_INVOICES) : null),
    [open, unique_invoices, searchQuery, context, value]
  )
  const visible_invoices = invoice_list?.visible ?? []
  const matched_count = invoice_list?.matched_count ?? 0
  const hidden_count = matched_count - visible_invoices.length

  if (!orgSettings.accounting_integration) {
    return null
  }

  function handle_select(inv: AccountingInvoiceJson) {
    if (reservedByOtherRows.has(inv.id)) {
      return
    }
    onChange(inv.id)
    setTimeout(() => setOpen(false), 0)
  }

  function handle_clear() {
    onChange(null)
  }

  function handle_search_change(v: string) {
    setSearchQuery(v)
    if (commandListRef.current) {
      commandListRef.current.scrollTop = 0
    }
  }

  function format_invoice_date(dateStr?: string): string {
    if (!dateStr) {
      return ""
    }
    try {
      return format(parseISO(dateStr), "MM/dd/yyyy")
    } catch {
      return dateStr
    }
  }

  return (
    <div class={`flex min-w-0 max-w-full items-center gap-2 ${className ?? ""}`}>
      <div class="min-w-0 flex-1">
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={`w-full justify-start text-left font-normal min-w-0 h-auto py-2 ${
                current ? "bg-white border-blue-200" : "text-gray-700"
              }`}>
            <div class="flex items-center gap-2 min-w-0 w-full">
              {current ? (
                <>
                  <Check className="h-4 w-4 shrink-0 text-green-600" />
                  <span class="truncate text-sm font-medium">{current.invoice_number}</span>
                  <span class="text-sm text-gray-500 shrink-0 tabular-nums">
                    {exactCurrencyFormatter(current.total_amount)}
                  </span>
                </>
              ) : (
                <>
                  <ChevronsUpDown className="h-4 w-4 shrink-0" />
                  <span class="text-gray-500 truncate">Select {integration_label} invoice…</span>
                </>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(28rem,calc(100vw-2rem))] p-0 overflow-hidden" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${integration_label} invoices…`}
              value={searchQuery}
              onValueChange={handle_search_change}
            />
            <CommandList ref={commandListRef} class="max-h-[min(24rem,50vh)]">
              <CommandEmpty>No invoices found.</CommandEmpty>
              <CommandGroup class="p-1.5">
                {visible_invoices.map((inv) => {
                  const is_selected = inv.id === value
                  const is_reserved_elsewhere = reservedByOtherRows.has(inv.id)
                  const shared_room_cents = is_selected ? undefined : sharedRoomCents?.get(inv.id)
                  const score = score_accounting_invoice_match(context, inv)
                  const show_recommended = score >= RECOMMENDED_INVOICE_BADGE_MIN_SCORE
                  const outstanding_tag = invoice_outstanding_tag(inv)
                  const links_note = describe_invoice_links(linkSummaries?.get(inv.id))

                  return (
                    <CommandItem
                      key={inv.id}
                      value={inv.id}
                      disabled={is_reserved_elsewhere}
                      className={`rounded-md border border-transparent mb-0.5 px-3 py-2.5 data-[selected=true]:bg-slate-100 ${
                        is_selected ? "!bg-sky-50 !border-sky-200" : ""
                      } ${is_reserved_elsewhere ? "opacity-50" : ""}`}
                      onSelect={() => !is_reserved_elsewhere && handle_select(inv)}>
                      <div class="flex w-full min-w-0 flex-col gap-1.5">
                        <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                          <div class="flex min-w-0 items-center gap-2">
                            <FileText className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                            <span class="font-mono text-sm font-semibold tabular-nums tracking-tight text-slate-900 truncate">
                              {inv.invoice_number}
                            </span>
                            {show_recommended ? (
                              <Badge variant="secondary" class="text-[10px] font-medium shrink-0">
                                Recommended
                              </Badge>
                            ) : null}
                          </div>
                          <span class="text-sm font-medium tabular-nums text-slate-800 shrink-0">
                            {exactCurrencyFormatter(inv.total_amount)}
                          </span>
                        </div>
                        <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600">
                          {inv.invoice_date ? (
                            <span class="shrink-0">{format_invoice_date(inv.invoice_date)}</span>
                          ) : null}
                          {outstanding_tag ? (
                            <span
                              class={`shrink-0 font-medium ${
                                outstanding_tag.tone === "paid" ? "text-red-700" : "text-amber-700"
                              }`}
                              title="This invoice is already paid or partly paid and can't take the full payment">
                              {outstanding_tag.label}
                            </span>
                          ) : null}
                          {is_reserved_elsewhere ? (
                            <span class="text-amber-800 shrink-0">Selected on another line</span>
                          ) : null}
                          {!is_reserved_elsewhere && shared_room_cents !== undefined ? (
                            <span
                              class="text-sky-800 shrink-0"
                              title="Another line of this check pays part of this invoice. Selecting it here pays the rest from this line.">
                              Also on another line · {exactCurrencyFormatter(shared_room_cents / 100)} left
                            </span>
                          ) : null}
                          {links_note ? (
                            <span
                              class="text-sky-800 shrink-0"
                              title="Another check pays part of this invoice. Selecting it here settles this check's share; the rest stays due until that check is applied.">
                              {links_note}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
              {hidden_count > 0 ? (
                <div class="border-t px-3 py-2 text-center text-xs text-slate-500">
                  Showing the top {visible_invoices.length} of {matched_count}. Type to narrow the results.
                </div>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      </div>
      {current ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          class="h-9 w-9 shrink-0"
          onClick={handle_clear}
          title="Clear selection"
          disabled={disabled}>
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}
